import { zipSync } from 'fflate';
import type { PanelDesign, PanelMesh, WallMesh } from '../types';
import { buildWallMesh, wallMeshStats, type MeshStats } from '../mesh/panelMesh';
import { exportBinaryStl } from './stl';
import { exportThreeMf, type ThreeMfObject } from './threemf';
import type { Placement } from './placement';
import { ledType, panelMaterial } from '../led/light';

/**
 * Export orchestration.
 *
 * Turns a design into a downloadable file. Two decisions shape the output:
 *
 * - **Format.** STL is a plain triangle soup that every slicer reads; 3MF keeps
 *   the panels as named objects and records millimetres explicitly. 3MF is the
 *   better choice for a whole wall, STL for a single part.
 * - **Arrangement.** `wall` keeps the panels at their wall coordinates so the
 *   slicer shows the design as it will hang; `plate` spreads them out with
 *   clearance so they can be printed straight away.
 */

export type ExportFormat = 'stl' | '3mf';
export type ExportArrangement = 'wall' | 'plate';
export type ExportBundle = 'single' | 'per-panel';

export interface ExportRequest {
  format: ExportFormat;
  /** `single` writes one file; `per-panel` writes a zip with one file per panel. */
  bundle: ExportBundle;
  arrangement: ExportArrangement;
  /** Restricts the export to one panel, by index into the wall layout. */
  panelIndex?: number;
  /** Clearance between parts for the `plate` arrangement, in millimetres. */
  plateSpacing?: number;
  /** Overrides the design's mesh resolution. */
  segments?: number;
  /** Adds the design JSON to a zip bundle so the export can be reopened. */
  includeDesign?: boolean;
  onProgress?: (completed: number, total: number) => void;
}

export interface ExportResult {
  filename: string;
  mimeType: string;
  data: Uint8Array;
  stats: MeshStats;
}

export const MIME_TYPES: Record<string, string> = {
  stl: 'model/stl',
  '3mf': 'model/3mf',
  zip: 'application/zip',
};

/**
 * Lowercase, dash-separated, safe on every filesystem.
 *
 * Accents are decomposed and their combining marks dropped, so "Übergröße"
 * becomes "ubergrosse" rather than collapsing into a row of dashes. Sharp s has
 * no decomposition of its own and is spelled out.
 */
export function slugify(value: string): string {
  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00df/g, 'ss')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug.slice(0, 60) : 'panel';
}

function panelLabel(panel: PanelMesh): string {
  return `panel-c${panel.panel.column + 1}-r${panel.panel.row + 1}`;
}

function frameLabel(frame: PanelMesh): string {
  return `frame-c${frame.panel.column + 1}-r${frame.panel.row + 1}`;
}

/**
 * Every part in the wall, with the name it is exported under.
 *
 * A backlit wall has two parts per panel — the panel itself and the light-box
 * frame behind it — and they are different prints: the panel wants translucent
 * filament, the frame wants whatever is cheap and preferably white, for the
 * reflection. Keeping them as distinct named objects is most of why 3MF is the
 * better format for a backlit design.
 */
function collectParts(wall: WallMesh): Array<{ name: string; part: PanelMesh }> {
  return [
    ...wall.panels.map((part) => ({ name: panelLabel(part), part })),
    ...wall.frames.map((part) => ({ name: frameLabel(part), part })),
  ];
}

/**
 * Placement of each panel for the chosen arrangement.
 *
 * `wall` keeps the panels where the design puts them, orientation included, so
 * a slicer shows the design as it will hang. `plate` re-packs them into a
 * square-ish grid with clearance and deliberately drops the rotation: a part on
 * the bed wants its canonical orientation, not the wall's.
 */
function placements(
  parts: ReadonlyArray<{ name: string; part: PanelMesh }>,
  wall: WallMesh,
  arrangement: ExportArrangement,
  spacing: number,
): Placement[] {
  if (arrangement === 'wall') {
    // A panel sits on top of its frame, so the assembly shows the real
    // stack-up instead of two parts occupying the same space.
    const lift = wall.frames.length > 0 ? wall.frames[0].bounds.max[2] : 0;
    return parts.map(({ part }, index) => ({
      x: part.panel.centerX,
      y: part.panel.centerY,
      z: index < wall.panels.length ? lift : 0,
      rotation: part.panel.rotation,
    }));
  }

  const columns = Math.max(1, Math.ceil(Math.sqrt(parts.length)));
  return parts.map(({ part }, index) => {
    const width = part.bounds.max[0] - part.bounds.min[0];
    const height = part.bounds.max[1] - part.bounds.min[1];
    const column = index % columns;
    const row = Math.floor(index / columns);
    return {
      x: column * (width + spacing),
      y: row * (height + spacing),
      z: 0,
      rotation: 0,
    };
  });
}

export function buildExport(design: PanelDesign, request: ExportRequest): ExportResult {
  const wall = buildWallMesh(design, {
    segments: request.segments,
    panelFilter:
      request.panelIndex === undefined
        ? undefined
        : (_panel, index) => index === request.panelIndex,
    onProgress: request.onProgress,
  });

  if (wall.panels.length === 0) {
    throw new Error('Nothing to export: the wall has no panels.');
  }

  const parts = collectParts(wall);
  const stats = wallMeshStats(wall);
  const base = `${slugify(design.name)}-${design.grid.columns}x${design.grid.rows}-${Math.round(
    design.panel.size,
  )}mm`;
  const spacing = request.plateSpacing ?? 5;
  const layout = placements(parts, wall, request.arrangement, spacing);

  if (request.bundle === 'per-panel' && parts.length > 1) {
    return {
      ...bundlePerPart(design, wall, request, base),
      stats,
    };
  }

  const singleName = parts.length === 1 ? `${base}-${parts[0].name}` : `${base}-wall`;

  if (request.format === 'stl') {
    const buffer = exportBinaryStl(
      parts.map(({ part }) => part.mesh),
      { header: `Organic3D ${design.name}`, placements: layout },
    );
    return {
      filename: `${singleName}.stl`,
      mimeType: MIME_TYPES.stl,
      data: new Uint8Array(buffer),
      stats,
    };
  }

  const objects: ThreeMfObject[] = parts.map(({ name, part }, index) => ({
    name,
    mesh: part.mesh,
    placement: layout[index],
  }));

  return {
    filename: `${singleName}.3mf`,
    mimeType: MIME_TYPES['3mf'],
    data: exportThreeMf(objects, {
      title: design.name,
      application: 'Organic3D',
      description: describeDesign(design),
    }),
    stats,
  };
}

function bundlePerPart(
  design: PanelDesign,
  wall: WallMesh,
  request: ExportRequest,
  base: string,
): Omit<ExportResult, 'stats'> {
  const files: Record<string, Uint8Array> = {};
  const encoder = new TextEncoder();

  for (const { name, part } of collectParts(wall)) {
    if (request.format === 'stl') {
      files[`${name}.stl`] = new Uint8Array(
        exportBinaryStl([part.mesh], { header: `Organic3D ${name}` }),
      );
    } else {
      files[`${name}.3mf`] = exportThreeMf([{ name, mesh: part.mesh }], {
        title: `${design.name} — ${name}`,
        application: 'Organic3D',
      });
    }
  }

  files['README.txt'] = encoder.encode(buildReadme(design, wall));
  if (request.includeDesign !== false) {
    files['design.json'] = encoder.encode(JSON.stringify(design, replacer, 2));
  }

  return {
    filename: `${base}-panels.zip`,
    mimeType: MIME_TYPES.zip,
    data: zipSync(files, { level: 6 }),
  };
}

/** Float32Array assets are not JSON-serialisable on their own. */
function replacer(_key: string, value: unknown): unknown {
  if (value instanceof Float32Array) return Array.from(value);
  return value;
}

export function describeDesign(design: PanelDesign): string {
  const { panel, grid, source, led } = design;
  return (
    `${grid.columns}x${grid.rows} ${panel.shapeId} panels, ${panel.size} mm, ` +
    `${panel.baseThickness} mm base + ${panel.reliefDepth} mm relief, ` +
    `texture: ${source.label}` +
    (led.enabled ? `, ${led.layout} LED channels` : '')
  );
}

/**
 * Wiring and optics notes.
 *
 * Whoever assembles the wall buys strip, plans runs and picks filament before
 * the parts come off the printer, so the numbers that decide those belong in
 * the file they already have. The optical checks are repeated here because a
 * design can print perfectly and still light badly, and the readme is what
 * survives being passed to somebody else.
 */
function ledSection(design: PanelDesign, wall: WallMesh): string[] {
  if (!wall.backlight) {
    return design.led.enabled
      ? [
          'BACKLIGHTING',
          '  Requested, but no frame could be built: the frame wall is too thick',
          '  for this panel footprint. Reduce it and export again.',
          '',
        ]
      : [];
  }

  const report = wall.backlight;
  const led = design.led;
  const type = ledType(led.ledType);
  const material = panelMaterial(led.panelMaterial);
  const metres = (mm: number): string => (mm / 1000).toFixed(2);
  const percent = (value: number): string => `${(value * 100).toFixed(0)}%`;

  const channel = report.channel;
  const lines = [
    'BACKLIGHTING',
    `  Mounting:        ${channel ? 'channel in the panel, one part per tile' : 'light box, two parts per tile'}`,
    `  Strip:           ${type.label}, ${led.stripWidth} mm wide`,
    `  Routing:         ${led.layout}${led.passThrough ? ', continuous between tiles' : ', closed per tile'}`,
    ...(channel
      ? [
          `  Tape seat:       ${channel.seatMm.toFixed(1)} mm deep, ${led.stripWidth} mm wide`,
          `  Emitter pocket:  ${channel.emitterMm.toFixed(1)} mm deeper, ${channel.emitterWidthMm.toFixed(1)} mm wide, on the centreline`,
          `  Groove:          ${channel.depthMm.toFixed(1)} mm at its deepest, ${channel.diffuserMm.toFixed(1)} mm of panel left over it`,
        ]
      : [`  Air gap:         ${report.airGapMm} mm from strip to panel`]),
    `  Strip per panel: ${metres(report.stripPerPanelMm)} m`,
    `  Strip total:     ${metres(report.totalStripMm)} m`,
    `  Power at white:  about ${report.wattsTotal.toFixed(0)} W`,
    `  Assembly depth:  ${report.assemblyDepthMm.toFixed(1)} mm from the wall`,
    '',
    '  Parts',
    '    panel-*  the relief. Print in a translucent or white filament, or no',
    `             light reaches the front. This design assumes ${material.label}.`,
    ...(channel
      ? [
          '             The groove is on the back and prints as a bridge over its',
          '             own opening, so it needs no supports. It is cut in two',
          '             steps: a seat the width of the tape, and a narrower',
          '             pocket down the middle for the emitters to stand in.',
        ]
      : [
          '    frame-*  the light box. Any filament; white reflects best. The strip',
          '             sits on the floor, facing the panel.',
        ]),
    '',
    '  Light check',
  ];

  if (channel) {
    lines.push(
      report.blending.ok
        ? '    Emitters blend: yes, this strip is continuous.'
        : `    Emitters blend: NO. A channel holds them ${channel.diffuserMm.toFixed(1)} mm under the` +
            ` surface and blending needs about ${Math.ceil(report.blending.requiredMm)} mm;` +
            ' a COB strip or the light-box mounting would fix it.',
    );
  } else {
    lines.push(
      report.blending.ok
        ? `    Emitters blend: yes, ${report.blending.actualMm} mm gap against ${report.blending.requiredMm.toFixed(0)} mm needed.`
        : `    Emitters blend: NO. A ${report.blending.actualMm} mm gap will show individual LEDs;` +
            ` ${Math.ceil(report.blending.requiredMm)} mm or a COB strip would fix it.`,
    );
  }
  lines.push(
    report.uniformity.ok
      ? `    Even coverage:  yes, runs ${report.uniformity.actualMm.toFixed(0)} mm apart against ${report.uniformity.requiredMm.toFixed(0)} mm allowed.`
      : `    Even coverage:  NO. Runs ${report.uniformity.actualMm.toFixed(0)} mm apart will band at this gap;` +
          ` ${report.uniformity.requiredMm.toFixed(0)} mm or closer would fix it.`,
  );
  const light = report.transmission;
  if (material.opaque) {
    // Naming a printable backing plate for an opaque filament would be advice
    // nobody can take: the material is the thing that has to change.
    lines.push(
      `    Through panel:  NO. ${material.label} passes ${percent(light.brightest)} even where the` +
        ' panel is thinnest. Print the panels in a white or translucent filament.',
    );
  } else if (!light.ok) {
    lines.push(
      `    Through panel:  NO. Only ${percent(light.brightest)} reaches the front at the thinnest` +
        ` point; ${material.label} needs a backing plate under ${light.maxBaseMm.toFixed(1)} mm.`,
    );
  } else if (light.peaksLit) {
    lines.push(
      `    Through panel:  yes, ${percent(light.brightest)} through the backing plate and` +
        ` ${percent(light.darkest)} at the deepest relief, so the whole surface lights.`,
    );
  } else {
    lines.push(
      `    Through panel:  ${percent(light.brightest)} in the valleys but ${percent(light.darkest)}` +
        ` at the peaks, so the relief reads as a silhouette. Relief under` +
        ` ${light.maxReliefMm.toFixed(1)} mm would light the whole surface.`,
    );
  }

  lines.push(
    '',
    '  Assembly',
    ...(channel
      ? [
          '    Press the strip into the groove on the back of each panel, emitters',
          '    first so they sit in the middle pocket and the tape rests on the',
          '    shoulders either side, and pass it through the notches into the',
          '    next panel. They hang flat: the strip is inside the panel.',
        ]
      : [
          '    Fix the frames to the wall, lay the strip on their floors and pass it',
          '    through the slots between boxes, then set the panels on top. The frames',
          '    have no screw holes: bond them, or drill the floor where you want one.',
        ]),
    '',
  );

  return lines;
}

function buildReadme(design: PanelDesign, wall: WallMesh): string {
  const stats = wallMeshStats(wall);
  const { panel, grid } = design;

  return [
    `Organic3D — ${design.name}`,
    '='.repeat(40),
    '',
    describeDesign(design),
    '',
    'Wall',
    `  Grid:            ${grid.columns} x ${grid.rows} panels`,
    `  Overall size:    ${wall.wall.width.toFixed(1)} x ${wall.wall.height.toFixed(1)} mm`,
    `  Mounting gap:    ${panel.gap} mm`,
    '',
    'Panel',
    `  Shape:           ${panel.shapeId}`,
    `  Size:            ${panel.size} mm`,
    `  Base thickness:  ${panel.baseThickness} mm`,
    `  Relief depth:    ${panel.reliefDepth} mm`,
    `  Total thickness: ${(panel.baseThickness + panel.reliefDepth).toFixed(2)} mm`,
    `  Edge treatment:  ${panel.edge.mode}${
      panel.edge.mode === 'seamless' ? '' : ` (${panel.edge.width} mm)`
    }`,
    `  Mesh resolution: ${panel.segments} segments per edge`,
    '',
    'Mesh',
    `  Parts:           ${stats.panels}`,
    `  Triangles:       ${stats.triangles.toLocaleString('en-US')}`,
    `  Solid volume:    ${stats.volumeCm3.toFixed(1)} cm3`,
    '',
    ...ledSection(design, wall),
    'Printing',
    '  Panels are exported face up with the backing plate on z = 0, so they',
    '  print flat on the bed without supports. Relief profiles stay within',
    '  printable overhangs as long as the relief depth is not pushed far past',
    '  the feature size of the pattern.',
    ...(wall.backlight
      ? [
          '',
          '  Frames print open side up, so they need no supports and no bridging.',
          '  The slots in their walls are open at the top for the same reason.',
        ]
      : []),
    '',
    '  design.json can be loaded back into Organic3D to change the design.',
    '',
    'Licensed under the MIT License.',
    '',
  ].join('\n');
}

export * from './stl';
export * from './threemf';
export * from './placement';
